import { useEffect, useRef } from 'react';
import { type MediaHandlerProps, useMediaUtils } from '..';

type VimeoType = {
  Player: (iframe: string | HTMLIFrameElement, options?: unknown) => void;
};

type Player = {
  // listeners
  on: (event: OnEvent, callback: (data: OnEventData) => void) => void;
  off: (event: OnEvent, callback: (data: OnEventData) => void) => void;

  // actions
  play: () => Promise<void>;
  pause: () => Promise<void>;

  // getters
  getAutoPause: () => Promise<boolean>;
  getCurrentTime: () => Promise<number>;
  getDuration: () => Promise<number>;
  getEnded: () => Promise<boolean>;
  getLoop: () => Promise<boolean>;
  getPaused: () => Promise<boolean>;
  getPlaybackRate: () => Promise<number>;
  getVolume: () => Promise<number>;
  getVideoTitle: () => Promise<string>;

  // setters
  setColor: (color: string) => Promise<string>;
};

type OnEvent =
  | 'bufferend'
  | 'bufferstart'
  | 'ended'
  | 'error'
  | 'loaded'
  | 'pause'
  | 'play'
  | 'playbackratechange'
  | 'progress'
  | 'seeked'
  | 'timeupdate'
  | 'volumechange';

type OnEventData = {
  duration?: number; // seconds
  seconds?: number;
  percent?: number;
  message?: string;
  method?: string;
  name?: string;
  id?: number;
  playbackRate?: number;
};

export const useVimeoHandler = ({ eventName }: MediaHandlerProps) => {
  const Vimeo = useRef<VimeoType | null>(null);
  const player = useRef<Player | null>(null);

  const { isFirstPlay, push, getIframe, getPercentage, playbackDuration, isScriptLoaded, loadScript } =
    useMediaUtils(eventName);

  const initPlayer = () => {
    const iframe = getIframe();
    if (!iframe) return;

    if (!Vimeo?.current) return;
    // @ts-ignore
    player.current = new Vimeo.current.Player(iframe);

    player.current?.getDuration().then((duration: number) => {
      if (playbackDuration.current === 0) playbackDuration.current = duration;
    });

    player?.current?.on('play', () => {
      if (isFirstPlay.current) return;
      isFirstPlay.current = true;
      push('play');
    });

    player?.current?.on('timeupdate', (data: OnEventData) => {
      // double check duration
      if (!playbackDuration.current) {
        // get duration from data
        if (data?.duration) playbackDuration.current = data.duration;
        // otherwise get duration from player
        else {
          player?.current?.getDuration().then((duration) => {
            playbackDuration.current = duration;
          });
          return;
        }
      }

      if (!isFirstPlay.current) {
        // double check first play
        isFirstPlay.current = true;
        push('play');
        return;
      }

      const percent = getPercentage(data?.seconds ?? 0, playbackDuration.current);
      if (!!percent) {
        push(percent);
      }
    });
  };

  useEffect(() => {
    if (!isScriptLoaded.current || Vimeo?.current === null) {
      loadScript('https://player.vimeo.com/api/player.js', () => {
        Vimeo.current = window?.Vimeo;
        initPlayer();
      });
    } else {
      initPlayer();
    }

    return () => {};
  }, []);
};
